import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';

import { environment } from '@env';
import { type CurrentUser, type UserRole } from './auth.models';
import { IdentityApiService } from './identity-api.service';

const POST_LOGIN_REDIRECT_KEY = 'vasta.analytics.postLoginRedirect';
const LOGOUT_BROADCAST_KEY = 'vasta.analytics.logoutBroadcast';

const LOCAL_DEMO_USER: CurrentUser = {
  id: 'local-demo-user',
  displayName: 'Локальный администратор',
  email: 'admin@localhost',
  role: 'admin',
  organizations: ['Завидово Парк'],
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauth = inject(OAuthService);
  private readonly identityApi = inject(IdentityApiService);
  private readonly router = inject(Router);

  readonly initializing = signal(true);
  readonly currentUser = signal<CurrentUser | null>(null);
  readonly isAdmin = computed(() => this.currentUser()?.role === 'admin');
  readonly keycloakEnabled = environment.authEnabled;

  constructor() {
    window.addEventListener('storage', (event) => {
      if (event.key === LOGOUT_BROADCAST_KEY && this.currentUser()) {
        this.currentUser.set(null);
        window.location.reload();
      }
    });
  }

  async initialize(): Promise<void> {
    if (!this.keycloakEnabled) {
      this.currentUser.set(LOCAL_DEMO_USER);
      this.initializing.set(false);
      return;
    }

    this.oauth.configure(environment.keycloak);
    this.oauth.setupAutomaticSilentRefresh();

    try {
      await this.oauth.loadDiscoveryDocumentAndTryLogin();

      if (this.oauth.hasValidAccessToken()) {
        const tokenUser = this.userFromClaims();
        this.currentUser.set(await this.enrichUser(tokenUser));
        await this.consumePostLoginRedirect();
      }
    } finally {
      this.initializing.set(false);
    }
  }

  login(): void {
    if (!this.keycloakEnabled) {
      this.currentUser.set(LOCAL_DEMO_USER);
      return;
    }

    this.oauth.initLoginFlow();
  }

  logout(): void {
    if (!this.keycloakEnabled) {
      this.currentUser.set(LOCAL_DEMO_USER);
      return;
    }

    this.currentUser.set(null);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    this.oauth.logOut();
  }

  isAuthenticated(): boolean {
    return this.keycloakEnabled ? this.oauth.hasValidAccessToken() : this.currentUser() !== null;
  }

  token(): string | null {
    if (!this.keycloakEnabled) {
      return null;
    }

    return this.oauth.getAccessToken() || null;
  }

  rememberPostLoginUrl(url: string): void {
    if (url.startsWith('/') && url !== '/') {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, url);
    }
  }

  async tryRefresh(): Promise<boolean> {
    if (!this.keycloakEnabled) {
      return false;
    }

    try {
      await this.oauth.refreshToken();
      return this.oauth.hasValidAccessToken();
    } catch {
      return false;
    }
  }

  private userFromClaims(): CurrentUser {
    const claims = (this.oauth.getIdentityClaims() ?? {}) as Record<string, unknown>;
    const roles = this.readRoles();

    return {
      id: String(claims['sub'] ?? ''),
      displayName: String(claims['name'] ?? claims['preferred_username'] ?? 'Пользователь'),
      email: String(claims['email'] ?? ''),
      role: this.normalizeRole(roles[0]),
      organizations: [],
    };
  }

  private async enrichUser(fallback: CurrentUser): Promise<CurrentUser> {
    try {
      const user = await this.identityApi.getCurrentUser();

      return {
        id: user.keycloakUserId || fallback.id,
        displayName: user.displayName || fallback.displayName,
        email: user.email || fallback.email,
        role: this.normalizeRole(user.role),
        organizations: user.organizations ?? fallback.organizations,
      };
    } catch {
      return fallback;
    }
  }

  private readRoles(): string[] {
    const payloadPart = this.oauth.getAccessToken().split('.')[1];
    if (!payloadPart) {
      return [];
    }

    try {
      const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))) as {
        realm_access?: { roles?: string[] };
        resource_access?: Record<string, { roles?: string[] }>;
      };

      return [
        ...(payload.realm_access?.roles ?? []),
        ...(payload.resource_access?.['analytics-ui']?.roles ?? []),
      ];
    } catch {
      return [];
    }
  }

  private normalizeRole(role: string | undefined): UserRole {
    const normalized = role?.toLowerCase();
    if (normalized === 'admin' || normalized === 'analyst') {
      return normalized;
    }
    return 'viewer';
  }

  private async consumePostLoginRedirect(): Promise<void> {
    const target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

    if (target?.startsWith('/')) {
      await this.router.navigateByUrl(target);
    }
  }
}
